import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const progressVariants = cva("relative w-full overflow-hidden", {
  variants: {
    variant: {
      default: "bg-primary/20 h-2 rounded-full",
      // Work started but no progress to show yet: a muted, pulsing track.
      pending: "bg-muted rounded-lg animate-pulse",
    },
  },
  defaultVariants: { variant: "default" },
});

/**
 * Progress of a task as a bar. `pending` is the track shown before there is progress.
 *
 * @import import { Progress } from "@/components/shadcn/progress";
 */
function Progress({
  className,
  value,
  variant,
  indicatorClassName,
  ...props
}: React.ComponentProps<typeof ProgressPrimitive.Root> &
  VariantProps<typeof progressVariants> & {
    indicatorClassName?: string;
  }) {
  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn(progressVariants({ variant }), className)}
      {...props}
    >
      {variant !== "pending" && (
        <ProgressPrimitive.Indicator
          data-slot="progress-indicator"
          className={cn(
            "bg-primary h-full w-full flex-1 transition-all",
            indicatorClassName
          )}
          style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
        />
      )}
    </ProgressPrimitive.Root>
  );
}

export { Progress };
